/**
 * Tonnie Languages Academy — the citizen's Academy endpoints
 * (`/api/heroes/qultureup/academy`). Every value is the server's: whether the
 * Academy opens, the consent asked, the levels the school would be told.
 *
 * Each call names the journey it is made from (`?journeyId` — the active one;
 * omitted, the server reads the primary): the Academy opens after THAT
 * journey's QultureUp assessment.
 */
import type { LanguageAcademyEntry, LanguageAcademyState } from '@tonnie/types';
import type { LanguageAcademyConsentInput, LanguageAcademyLaunchInput } from '@tonnie/schemas';

import { TonnieClient } from '../client/base';

const BASE = '/api/heroes/qultureup/academy';

/** `path` with the journey named, when there is one. */
function onJourney(path: string, journeyId?: string): string {
  return journeyId ? `${path}?journeyId=${encodeURIComponent(journeyId)}` : path;
}

export class LanguageAcademyService {
  constructor(private client: TonnieClient) {}

  /** The Academy as the World shows it, for the journey. */
  getState(journeyId?: string): Promise<LanguageAcademyState> {
    return this.client.get<LanguageAcademyState>(onJourney(BASE, journeyId));
  }

  /** Accept the consent text the state names (`consent.currentVersion`) — answers the journey's state. */
  giveConsent(input: LanguageAcademyConsentInput, journeyId?: string): Promise<LanguageAcademyState> {
    return this.client.post<LanguageAcademyState>(onJourney(`${BASE}/consent`, journeyId), input);
  }

  /** Withdraw it — the school can no longer read anything of the citizen. Answers the journey's state. */
  revokeConsent(journeyId?: string): Promise<LanguageAcademyState> {
    return this.client.delete<LanguageAcademyState>(onJourney(`${BASE}/consent`, journeyId));
  }

  /**
   * Open the school in a language: the address with a one-time code (60 s).
   * Open it in a new tab at once — the tab opened on the click, the address
   * given to it when this answers, so it is not blocked as a pop-up.
   */
  launch(input: LanguageAcademyLaunchInput, journeyId?: string): Promise<LanguageAcademyEntry> {
    return this.client.post<LanguageAcademyEntry>(onJourney(`${BASE}/launch`, journeyId), input);
  }
}

export function createLanguageAcademyService(client: TonnieClient): LanguageAcademyService {
  return new LanguageAcademyService(client);
}
